import React, { FC, useEffect, useState } from "react";
import styled from "styled-components";
import { MenuItem } from "@blueprintjs/core";
import { ItemRenderer, MultiSelect } from "@blueprintjs/select";

import { submitDeploymentRepo } from "@api";
import { AppToaster } from "@components/AppToaster";
import { useOrg } from "@hooks";

import { Repo } from "@pages/projects/types";

// Arbitrary limits, match up with backend
const TAG_LIMIT = 10;
const TAG_CHAR_LIMIT = 64;

interface TagSelectorProps {
  tags: string[];
  selectedTags: string[];
  repo: Repo;
}

const Wrapper = styled.div`
  width: 50%;
`;

const Component = MultiSelect.ofType<string>();

const RenderCreateTagOption = (
  query: string,
  active: boolean,
  handleClick: React.MouseEventHandler<HTMLElement>
) => {
  return (
    <MenuItem
      icon="add"
      text={`Create tag "${query}"`}
      active={active}
      onClick={handleClick}
      shouldDismissPopover={false}
    />
  );
};

export const TagSelector: FC<TagSelectorProps> = ({
  tags: fetchedTags,
  selectedTags,
  repo,
}) => {
  const org = useOrg();
  const [tags, setTags] = useState<string[]>(fetchedTags);
  const [selected, setSelected] = useState<string[]>(selectedTags);
  const [hitLimit, setHitLimit] = useState<boolean>(false);
  const [query, setQuery] = useState<string>("");

  useEffect(() => {
    setHitLimit(selected.length >= TAG_LIMIT);
    setQuery("");
  }, [selected]);

  const handleTagSelect = async (tag: string) => {
    const hitCharLimit = tag.length > TAG_CHAR_LIMIT;
    if (hitCharLimit) {
      AppToaster.show({
        message: `Tag "${tag}" exceeds character limit of 64.`,
        intent: "warning",
      });
      return;
    }

    if (hitLimit) {
      AppToaster.show({
        message: `You can only select up to 10 tags. Please deselect some tags before adding "${tag}"`,
        intent: "warning",
      });
    }
    // Tag was created
    if (!tags.includes(tag) && !hitCharLimit) {
      setTags([...tags, tag]);
    }

    if (selected.includes(tag)) {
      setSelected(selected.filter((t) => t !== tag));
    } else {
      if (!hitLimit) {
        setSelected([...selected, tag]);
      }
    }

    if (!hitLimit && !hitCharLimit) {
      try {
        const tags = [...selected, tag];
        await submitDeploymentRepo(org?.id!, repo, { tags });
        AppToaster.show({
          message: `Successfully added tag "${tag}" to ${repo.name}`,
          intent: "success",
        });
      } catch (error) {
        AppToaster.show({
          message: `Failed to add tag "${tag}" to ${repo.name} - ${error}`,
          intent: "danger",
        });
      }
    }
  };

  const isSelected = (tag: string) => selected.includes(tag);

  const RenderTag: ItemRenderer<string> = (tag, { handleClick, modifiers }) => {
    if (!modifiers.matchesPredicate) {
      return null;
    }
    return (
      <MenuItem
        data-testid={`TagSelectorMenuItem-${tag}`}
        icon={isSelected(tag) ? "tick" : "blank"}
        text={tag}
        key={tag}
        onClick={handleClick}
        disabled={hitLimit && !selected.includes(tag)}
      />
    );
  };

  const handleTagRemove = async (_: React.ReactNode, index: number) => {
    const tag = selected[index];
    const tags = selected.filter((_, tagIndex) => index !== tagIndex);
    try {
      await submitDeploymentRepo(org?.id!, repo, { tags });
      AppToaster.show({
        message: `Successfully removed tag "${tag}" from ${repo.name}`,
        intent: "success",
      });
      setSelected(tags);
    } catch (error) {
      AppToaster.show({
        message: `Failed to remove tag "${tag}" from ${repo.name} - ${error}`,
        intent: "danger",
      });
    }
  };

  const handleFilter = (query: string) => {
    return tags.filter((tag) => tag.includes(query));
  };

  return (
    <Wrapper className="project-tag-select">
      <Component
        data-testid="TagSelector"
        items={tags}
        itemListPredicate={handleFilter}
        selectedItems={selected}
        tagRenderer={(tag) => <span data-testid="TagSelectorTag">{tag}</span>}
        itemRenderer={RenderTag}
        onItemSelect={handleTagSelect}
        onRemove={handleTagRemove}
        fill
        createNewItemFromQuery={(q) => q}
        createNewItemPosition="last"
        query={query}
        onQueryChange={(q) => setQuery(q)}
        noResults={
          <MenuItem
            data-testid="TagSelectorNoResults"
            text="No tags match this query. Start typing to create a new tag."
          />
        }
        createNewItemRenderer={(
          query: string,
          active: boolean,
          handleClick: React.MouseEventHandler<HTMLElement>
        ) => RenderCreateTagOption(query, active, handleClick)}
      />
    </Wrapper>
  );
};
